import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BrokerLayout from '@/components/BrokerLayout';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { formatPrice } from '@/components/PropertyCard';
import { Plus, Upload, ExternalLink, Building2 } from 'lucide-react';

const FALLBACK = 'https://images.unsplash.com/photo-1649989184479-73ab3b750a73?crop=entropy&cs=srgb&fm=jpg&q=85';

const BrokerProjects = () => {
  const [projects, setProjects] = useState([]);
  const [profile, setProfile] = useState(null);
  const [cities, setCities] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '', property_category: 'residential', project_type: 'apartment',
    project_status: 'new_launch', possession_date: '', rera_number: '',
    builder_name: '',
    state: '', city: '', locality: '', sector: '', address: '', pincode: '',
    price_min: '', price_max: '', configurations: '', unit_sizes: '', total_units: '',
    overview: '', highlights: '', amenities: '', location_advantages: '',
    cover_image_url: '', gallery_images: []
  });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const [p, prof, c] = await Promise.all([
        api.get('/broker/projects'),
        api.get('/broker/profile'),
        api.get('/public/cities')
      ]);
      setProjects(p.data);
      setProfile(prof.data);
      setCities(c.data);
    } catch (e) { console.error(e); }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`;
      setNewProject({ ...newProject, cover_image_url: url });
      toast.success('Cover image uploaded');
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const createProject = async () => {
    setSaving(true);
    try {
      const payload = {
        ...newProject,
        price_min: parseFloat(newProject.price_min),
        price_max: parseFloat(newProject.price_max),
        total_units: parseInt(newProject.total_units) || 0,
        configurations: newProject.configurations.split(',').map(s => s.trim()).filter(Boolean),
        highlights: newProject.highlights.split('\n').map(s => s.trim()).filter(Boolean),
        amenities: newProject.amenities.split(',').map(s => s.trim()).filter(Boolean),
        location_advantages: newProject.location_advantages.split('\n').map(s => s.trim()).filter(Boolean),
      };
      const { data } = await api.post('/projects', payload);
      // Auto submit
      await api.post(`/projects/${data.id}/submit`);
      toast.success('Project created and submitted for approval!');
      setOpen(false);
      setNewProject({
        name: '', property_category: 'residential', project_type: 'apartment',
        project_status: 'new_launch', possession_date: '', rera_number: '',
        builder_name: '',
        state: '', city: '', locality: '', sector: '', address: '', pincode: '',
        price_min: '', price_max: '', configurations: '', unit_sizes: '', total_units: '',
        overview: '', highlights: '', amenities: '', location_advantages: '',
        cover_image_url: '', gallery_images: []
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  const statusColor = (s) => {
    const map = {
      draft: 'bg-slate-100 text-slate-700',
      submitted: 'bg-amber-50 text-amber-700',
      published: 'bg-emerald-50 text-emerald-700',
      rejected: 'bg-red-50 text-red-700',
      archived: 'bg-slate-100 text-slate-500'
    };
    return map[s] || 'bg-slate-100 text-slate-700';
  };

  return (
    <BrokerLayout profile={profile}>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0F2C59]" data-testid="broker-projects-title">My Projects</h1>
          <p className="text-sm text-slate-500 mt-1">Manage builder projects and inventory</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#F97316] hover:bg-[#EA580C]" disabled={profile?.status !== 'approved'} data-testid="add-project-btn">
              <Plus size={16} className="mr-2"/> Add Project
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {/* Basic */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2"><Label>Project Name *</Label><Input value={newProject.name} onChange={(e) => setNewProject({...newProject, name: e.target.value})} data-testid="project-name-input" /></div>
                <div><Label>Builder Name</Label><Input value={newProject.builder_name} onChange={(e) => setNewProject({...newProject, builder_name: e.target.value})} data-testid="project-builder-input" /></div>
                <div><Label>RERA Number</Label><Input value={newProject.rera_number} onChange={(e) => setNewProject({...newProject, rera_number: e.target.value})} data-testid="project-rera-input" /></div>
                <div>
                  <Label>Category</Label>
                  <Select value={newProject.property_category} onValueChange={(v) => setNewProject({...newProject, property_category: v})}>
                    <SelectTrigger data-testid="project-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="plot">Plot</SelectItem>
                      <SelectItem value="land">Land</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={newProject.project_status} onValueChange={(v) => setNewProject({...newProject, project_status: v})}>
                    <SelectTrigger data-testid="project-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new_launch">New Launch</SelectItem>
                      <SelectItem value="under_construction">Under Construction</SelectItem>
                      <SelectItem value="ready_to_move">Ready to Move</SelectItem>
                      <SelectItem value="resale">Resale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Possession Date</Label><Input value={newProject.possession_date} onChange={(e) => setNewProject({...newProject, possession_date: e.target.value})} placeholder="Dec 2027" data-testid="project-possession-input" /></div>
                <div>
                  <Label>Project Type</Label>
                  <Input value={newProject.project_type} onChange={(e) => setNewProject({...newProject, project_type: e.target.value})} placeholder="apartment, villa, plot" data-testid="project-type-input" />
                </div>
              </div>

              {/* Location */}
              <div className="pt-3 border-t">
                <h4 className="font-semibold text-sm text-[#0F2C59] mb-3">Location</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label>City *</Label>
                    <Select value={newProject.city} onValueChange={(v) => {
                      const c = cities.find(x => x.name === v);
                      setNewProject({...newProject, city: v, state: c?.state || newProject.state});
                    }}>
                      <SelectTrigger data-testid="project-city-select"><SelectValue placeholder="Select"/></SelectTrigger>
                      <SelectContent>{cities.map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>State</Label><Input value={newProject.state} onChange={(e) => setNewProject({...newProject, state: e.target.value})} data-testid="project-state-input" /></div>
                  <div><Label>Locality *</Label><Input value={newProject.locality} onChange={(e) => setNewProject({...newProject, locality: e.target.value})} data-testid="project-locality-input" /></div>
                  <div><Label>Sector</Label><Input value={newProject.sector} onChange={(e) => setNewProject({...newProject, sector: e.target.value})} data-testid="project-sector-input" /></div>
                  <div><Label>Pincode</Label><Input value={newProject.pincode} onChange={(e) => setNewProject({...newProject, pincode: e.target.value})} data-testid="project-pincode-input" /></div>
                  <div className="md:col-span-3"><Label>Full Address</Label><Input value={newProject.address} onChange={(e) => setNewProject({...newProject, address: e.target.value})} data-testid="project-address-input" /></div>
                </div>
              </div>

              {/* Pricing */}
              <div className="pt-3 border-t">
                <h4 className="font-semibold text-sm text-[#0F2C59] mb-3">Pricing & Configuration</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label>Min Price (₹) *</Label><Input type="number" value={newProject.price_min} onChange={(e) => setNewProject({...newProject, price_min: e.target.value})} data-testid="project-price-min-input" /></div>
                  <div><Label>Max Price (₹) *</Label><Input type="number" value={newProject.price_max} onChange={(e) => setNewProject({...newProject, price_max: e.target.value})} data-testid="project-price-max-input" /></div>
                  <div><Label>Configurations (comma)</Label><Input value={newProject.configurations} onChange={(e) => setNewProject({...newProject, configurations: e.target.value})} placeholder="2BHK, 3BHK, 4BHK" data-testid="project-configurations-input" /></div>
                  <div><Label>Unit Sizes</Label><Input value={newProject.unit_sizes} onChange={(e) => setNewProject({...newProject, unit_sizes: e.target.value})} placeholder="1200-2400 sq ft" data-testid="project-unit-sizes-input" /></div>
                  <div><Label>Total Units</Label><Input type="number" value={newProject.total_units} onChange={(e) => setNewProject({...newProject, total_units: e.target.value})} data-testid="project-total-units-input" /></div>
                </div>
              </div>

              {/* Content */}
              <div className="pt-3 border-t">
                <h4 className="font-semibold text-sm text-[#0F2C59] mb-3">Content</h4>
                <div className="space-y-3">
                  <div><Label>Overview *</Label><Textarea value={newProject.overview} onChange={(e) => setNewProject({...newProject, overview: e.target.value})} rows={4} data-testid="project-overview-input" /></div>
                  <div><Label>Highlights (one per line)</Label><Textarea value={newProject.highlights} onChange={(e) => setNewProject({...newProject, highlights: e.target.value})} rows={3} placeholder="Premium fittings&#10;24x7 security&#10;Ready parking" data-testid="project-highlights-input" /></div>
                  <div><Label>Amenities (comma)</Label><Input value={newProject.amenities} onChange={(e) => setNewProject({...newProject, amenities: e.target.value})} placeholder="Swimming Pool, Gym, Clubhouse" data-testid="project-amenities-input" /></div>
                  <div><Label>Location Advantages (one per line)</Label><Textarea value={newProject.location_advantages} onChange={(e) => setNewProject({...newProject, location_advantages: e.target.value})} rows={3} placeholder="5 mins from metro&#10;Near IT hub" data-testid="project-location-adv-input" /></div>
                </div>
              </div>

              {/* Media */}
              <div className="pt-3 border-t">
                <h4 className="font-semibold text-sm text-[#0F2C59] mb-3">Cover Image</h4>
                {newProject.cover_image_url ? (
                  <div className="flex items-center gap-3">
                    <img src={newProject.cover_image_url} alt="cover" className="w-24 h-16 object-cover rounded-md border" />
                    <label className="text-xs text-[#F97316] cursor-pointer font-semibold">
                      Replace
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files[0])} />
                    </label>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-md py-6 cursor-pointer hover:border-[#F97316] text-sm text-slate-600" data-testid="upload-cover-input">
                    <Upload size={14} />
                    {uploading ? 'Uploading...' : 'Upload cover image'}
                    <input type="file" className="hidden" accept="image/*" onChange={(e) => uploadCover(e.target.files[0])} />
                  </label>
                )}
              </div>

              <Button onClick={createProject} disabled={saving} className="w-full bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="create-project-btn">
                {saving ? 'Creating...' : 'Create & Submit for Approval'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {profile?.status !== 'approved' && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
          Complete KYC and get admin approval before adding projects.
        </div>
      )}

      {projects.length === 0 ? (
        <div className="bg-white rounded-lg border border-dashed border-slate-300 p-12 text-center">
          <Building2 size={40} className="mx-auto text-slate-300 mb-3" />
          <div className="text-slate-500 font-medium">No projects yet</div>
          <div className="text-xs text-slate-400 mt-1">Click "Add Project" to create your first listing</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {projects.map(project => (
            <div key={project.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid={`project-card-${project.id}`}>
              <div className="h-40 bg-slate-100">
                <img src={project.cover_image_url || FALLBACK} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-bold text-slate-900 text-sm">{project.name}</h3>
                  <Badge className={`${statusColor(project.status)} text-xs`}>{project.status}</Badge>
                </div>
                <div className="text-xs text-slate-500 mt-1">{project.locality}, {project.city}</div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="font-mono font-bold text-[#0F2C59] text-sm">{formatPrice(project.price_min)}</div>
                  <Link to={`/project/${project.id}`} target="_blank" className="text-xs text-[#F97316] font-semibold hover:underline flex items-center gap-1">
                    View <ExternalLink size={11} />
                  </Link>
                </div>
                {project.admin_notes && (
                  <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-red-700">{project.admin_notes}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </BrokerLayout>
  );
};

export default BrokerProjects;
